// Plata. Todo en centavos enteros: nunca un float guarda un importe.
// Lo usan el navegador y el Worker, para que un total no pueda dar distinto en
// la pantalla y en la base (AGENTS.md seccion 2).

import type { Moneda } from "./tipos";

/**
 * Subtotal de un item, en centavos enteros.
 *
 * La cantidad si puede tener decimales (1,5 horas), asi que el producto se
 * redondea una sola vez, aca. Si no, 0.1 * 100000 daria 10000.000000000002 y
 * ese error se arrastraria a todos los totales.
 */
export function subtotal(cantidad: number, precioUnitario: number): number {
  return Math.round(cantidad * precioUnitario);
}

export interface ItemCalculable {
  cantidad: number;
  precio_unitario: number;
}

/** Total del presupuesto: suma de subtotales ya redondeados. */
export function totalPresupuesto(items: ItemCalculable[]): number {
  return items.reduce((acc, i) => acc + subtotal(i.cantidad, i.precio_unitario), 0);
}

/** Cantidad para mostrar: coma decimal y sin ceros de relleno. 2.5 -> '2,5' */
export function formatearCantidad(cantidad: number): string {
  return String(Math.round(cantidad * 1000) / 1000).replace(".", ",");
}

const SIMBOLO: Record<Moneda, string> = { UYU: "$", USD: "US$" };

/** Centavos -> '$ 1.234,56', al uso uruguayo: punto de miles, coma decimal. */
export function formatearMonto(centavos: number, moneda: Moneda = "UYU"): string {
  const negativo = centavos < 0;
  const abs = Math.abs(Math.round(centavos));
  const entero = Math.floor(abs / 100);
  const resto = abs % 100;
  const conPuntos = String(entero).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  const dec = resto < 10 ? "0" + resto : String(resto);
  return `${negativo ? "-" : ""}${SIMBOLO[moneda]} ${conPuntos},${dec}`;
}

/**
 * '1.234,56' -> 123456 centavos. Acepta lo que uno escribe de apuro: con o sin
 * puntos de miles, con coma o con punto decimal. Devuelve null si no es un
 * numero.
 */
export function parsearMonto(texto: string): number | null {
  const limpio = texto.trim().replace(/[^\d,.-]/g, "");
  if (!limpio) return null;

  // Si hay coma, manda la coma como separador decimal y los puntos son de miles.
  // Si no hay coma, un punto solo puede ser decimal (2500.50) o de miles (2.500):
  // se toma como decimal cuando deja exactamente dos digitos.
  let normal: string;
  if (limpio.includes(",")) {
    normal = limpio.replace(/\./g, "").replace(",", ".");
  } else {
    const partes = limpio.split(".");
    normal =
      partes.length === 2 && partes[1].length <= 2 ? limpio : limpio.replace(/\./g, "");
  }

  const n = Number(normal);
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 100);
}
